import { useState } from 'react';

interface Spec {
  icon: string;
  label: string;
  value: string;
}

interface ProductCardProps {
  title: string;
  images: string[];
  specs: Spec[];
  accentColor: 'orange' | 'blue';
  safetyFeature?: string;
  safetyTitle?: string;
  accessories?: string;
  accessoriesTitle?: string;
  onOpenLightbox: (images: string[], index: number) => void;
}

export default function ProductCard({
  title,
  images,
  specs,
  accentColor,
  safetyFeature,
  safetyTitle,
  accessories,
  accessoriesTitle,
  onOpenLightbox
}: ProductCardProps) {
  const [selectedImage, setSelectedImage] = useState(0);

  const colorClasses = {
    orange: {
      bg: 'bg-orange-500',
      text: 'text-orange-500',
      border: 'border-orange-500',
      ring: 'ring-orange-500',
      hover: 'hover:border-orange-500'
    },
    blue: {
      bg: 'bg-blue-600',
      text: 'text-blue-600',
      border: 'border-blue-600',
      ring: 'ring-blue-600',
      hover: 'hover:border-blue-600'
    }
  };

  const colors = colorClasses[accentColor];

  return (
    <div className="mb-16">
      <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8">{title}</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
        {/* Image Gallery */}
        <div>
          {/* Main Image */}
          <div 
            className="relative w-full h-64 sm:h-80 md:h-96 bg-gray-100 rounded-lg overflow-hidden mb-3 sm:mb-4 cursor-pointer group"
            onClick={() => onOpenLightbox(images, selectedImage)}
          >
            <img
              src={images[selectedImage]}
              alt={`${title} - Imagem ${selectedImage + 1}`}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
              <i className="ri-zoom-in-line text-4xl text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
            </div>
          </div>

          {/* Thumbnails */}
          <div className="grid grid-cols-4 gap-2 sm:gap-3">
            {images.map((image, index) => (
              <div
                key={index}
                onClick={() => setSelectedImage(index)}
                className={`relative w-full h-16 sm:h-20 md:h-24 bg-gray-100 rounded-lg overflow-hidden cursor-pointer border-2 transition-all duration-300 ${
                  selectedImage === index
                    ? `${colors.border} ring-2 ${colors.ring}`
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <img
                  src={image}
                  alt={`Miniatura ${index + 1}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 sm:p-1">
                  <i className={`${index === 3 ? 'ri-3d-line' : 'ri-camera-line'} text-xs sm:text-sm ${colors.text}`}></i>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Specifications */}
        <div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6">
            {specs.map((spec, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200 hover:shadow-md transition-shadow duration-300"
              >
                <div className="flex items-start gap-3">
                  <div className={`${colors.text} text-xl sm:text-2xl mt-1`}>
                    <i className={spec.icon}></i>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600 mb-1">{spec.label}</p>
                    <p className="text-base sm:text-lg font-bold text-gray-900">{spec.value}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {safetyFeature && (
            <div className={`${colors.bg} text-white rounded-lg p-5 sm:p-6`}>
              <div className="flex items-center gap-3 mb-2">
                <i className="ri-shield-check-line text-xl sm:text-2xl"></i>
                <h4 className="text-base sm:text-lg font-bold">{safetyTitle}</h4>
              </div>
              <p className="text-white/90 text-sm sm:text-base">{safetyFeature}</p>
            </div>
          )}

          {accessories && (
            <div className={`${colors.bg} text-white rounded-lg p-5 sm:p-6`}>
              <div className="flex items-center gap-3 mb-2">
                <i className="ri-tools-line text-xl sm:text-2xl"></i>
                <h4 className="text-base sm:text-lg font-bold">{accessoriesTitle}</h4>
              </div>
              <p className="text-white/90 text-sm sm:text-base">{accessories}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}